import BackupService from '@/services/backup'
import { chapterPersistenceOrchestrator } from '@/services/chapterPersistenceOrchestrator'
import ChaptersService from '@/services/chapters'
import ProjectsService from '@/services/projects'
import {
  getOfflineProject,
  listOfflineProjects,
  markProjectAvailableOffline,
  removeOfflineProject,
  requestPersistentStorage,
} from '@/storage'
import { ProjectType } from '@/constants/projectTypes'
import type { Chapter, ChapterListItem } from '@/types'
import type { ChapterBackup } from '@/types/editor'
import { describeChapterSyncFailure } from '@/utils/chapterErrors'
import { isBackupPendingUpload } from '@/utils/chapterSyncPhase'
import {
  countWordsInHtmlContent,
  countWordsInScreenplayJson,
  countWordsInTipTapJson,
} from '@/utils/chapterContentDeletion'
import isTipTapDoc from '@/utils/isTipTapDoc'
import { readProjectShell } from '@/utils/projectShellCache'

export interface DeviceChapterCopy {
  chapterId: string
  title: string
}

export interface DeviceProjectCopy {
  projectId: string
  title: string
  type: string
  status: string
  chapterCount: number
  chapters: DeviceChapterCopy[]
  /** Newest local write time, milliseconds. */
  backupAt: number
  lastServerChangeAt: string
  hasPendingChanges: boolean
}

function compareByOrderThenTitle(
  a: { order?: number; title: string },
  b: { order?: number; title: string },
): number {
  const aHasOrder = typeof a.order === 'number'
  const bHasOrder = typeof b.order === 'number'
  if (aHasOrder && bHasOrder && a.order !== b.order) {
    return (a.order as number) - (b.order as number)
  }
  if (aHasOrder !== bHasOrder) return aHasOrder ? -1 : 1
  return a.title.localeCompare(b.title, 'es')
}

function sortedDeviceChapters(
  chapters: { chapterId: string; title: string; order?: number }[],
): DeviceChapterCopy[] {
  const rows = chapters.map((chapter) => ({
    chapterId: chapter.chapterId,
    title: chapter.title || 'Sin título',
    order: chapter.order,
  }))

  rows.sort(compareByOrderThenTitle)

  return rows.map(({ chapterId, title }) => ({ chapterId, title }))
}

export async function listDeviceProjectCopies(): Promise<DeviceProjectCopy[]> {
  await BackupService.whenReady()
  const backups = BackupService.listChapterBackups()
  const byProject = new Map<string, typeof backups>()

  for (const backup of backups) {
    const list = byProject.get(backup.projectId) || []
    list.push(backup)
    byProject.set(backup.projectId, list)
  }

  const ids = new Set<string>([
    ...byProject.keys(),
    ...listOfflineProjects().map((record) => record.projectId),
  ])

  const copies: DeviceProjectCopy[] = []
  for (const projectId of ids) {
    const chapters = byProject.get(projectId) || []
    const pin = getOfflineProject(projectId)
    const shell = readProjectShell(projectId)
    const backupAt = chapters.reduce((max, chapter) => Math.max(max, chapter.timestamp), 0)
    const serverTimes = chapters
      .map((chapter) => chapter.baseUpdatedAt)
      .filter((value): value is string => Boolean(value))
      .sort()
    const serverChangeAt = serverTimes[serverTimes.length - 1]

    copies.push({
      projectId,
      title: pin?.title || shell?.title || 'Proyecto sin título',
      type: pin?.type || shell?.type || '',
      status: pin?.status || shell?.status || '',
      chapterCount: chapters.length,
      chapters: sortedDeviceChapters(chapters),
      backupAt,
      lastServerChangeAt: pin?.lastServerChangeAt || serverChangeAt || '',
      hasPendingChanges: chapters.some((chapter) => isBackupPendingUpload(chapter)),
    })
  }

  copies.sort((a, b) => b.backupAt - a.backupAt || a.title.localeCompare(b.title, 'es'))
  return copies
}

export async function listDeviceChapters(projectId: string): Promise<ChapterListItem[]> {
  await BackupService.whenReady()
  const rows = BackupService.listChapterBackups()
    .filter((backup) => backup.projectId === projectId)
    .map((backup) => ({
      chapterId: backup.chapterId,
      title: backup.title || 'Sin título',
      updatedAt: backup.baseUpdatedAt || backup.savedAt,
      order: backup.order,
    }))

  rows.sort(compareByOrderThenTitle)

  return rows.map(({ chapterId, title, updatedAt }) => ({
    chapterId,
    title,
    updatedAt,
  }))
}

export async function downloadProjectForOffline(input: {
  projectId: string
  title: string
  type: string
  status: string
}): Promise<void> {
  await BackupService.whenReady()
  void requestPersistentStorage()

  const response = await ProjectsService.listChapters(input.projectId)
  const listed = (response.data || []) as ChapterListItem[]
  let latestServerChange = ''

  for (const item of listed) {
    const chapterResponse = await ChaptersService.get(item.chapterId)
    const chapter = chapterResponse.data as Chapter
    BackupService.saveSyncedChapterCopy({
      projectId: input.projectId,
      chapterId: chapter._id,
      title: chapter.title || item.title || '',
      jsonContent: chapter.jsonContent || {},
      content: chapter.content,
      baseUpdatedAt: chapter.updatedAt,
      order: chapter.order,
    }, {
      touchProjectSync: false,
    })
    if (chapter.updatedAt && chapter.updatedAt > latestServerChange) {
      latestServerChange = chapter.updatedAt
    }
  }

  const serverIds = new Set(listed.map((item) => item.chapterId))
  for (const backup of BackupService.listChapterBackups()) {
    if (backup.projectId !== input.projectId) continue
    if (serverIds.has(backup.chapterId)) continue
    if (backup.pendingSync !== false) continue
    BackupService.removeChapterBackup(backup.projectId, backup.chapterId)
  }

  markProjectAvailableOffline({
    projectId: input.projectId,
    title: input.title,
    type: input.type,
    status: input.status,
    lastServerChangeAt: latestServerChange || undefined,
  })
}

export interface SyncChapterFailure {
  chapterId: string
  title: string
  reason: string
  wordsCount: number
  changedAt: string
}

function backupWordCount(backup: ChapterBackup, projectType: string): number {
  if (projectType === ProjectType.SCREENPLAY) {
    return countWordsInScreenplayJson(backup.jsonContent)
  }
  if (isTipTapDoc(backup.jsonContent)) {
    return countWordsInTipTapJson(backup.jsonContent)
  }
  if (backup.content) return countWordsInHtmlContent(backup.content)
  return 0
}

export interface SyncOfflineProjectResult {
  attempted: number
  failedChapters: SyncChapterFailure[]
}

export async function syncOfflineProject(projectId: string): Promise<SyncOfflineProjectResult> {
  await BackupService.whenReady()
  const pending = BackupService.listChapterBackups().filter(
    (backup) => backup.projectId === projectId && isBackupPendingUpload(backup),
  )
  const failedChapters: SyncChapterFailure[] = []
  const projectType = getOfflineProject(projectId)?.type
    || readProjectShell(projectId)?.type
    || ''

  for (const backup of pending) {
    const result = await chapterPersistenceOrchestrator.uploadChapter(backup)
    if (result.status === 'success') {
      continue
    }

    const reason =
      result.status === 'failed'
        ? result.message
        : describeChapterSyncFailure(
            result.status === 'needs_editor'
              ? {
                  response: {
                    data: { data: { reason: 'CHAPTER_CONTENT_DELETION_REQUIRES_FORCE' } },
                  },
                }
              : result.status === 'conflict'
                ? { response: { status: 409 } }
                : result.status === 'auth_error'
                  ? { response: { status: 403 } }
                  : new Error('sync failed'),
          )

    console.error('Error syncing offline chapter:', backup.chapterId, result)
    failedChapters.push({
      chapterId: backup.chapterId,
      title: backup.title || 'Capítulo sin título',
      reason,
      wordsCount: backupWordCount(backup, projectType),
      changedAt: backup.savedAt || new Date(backup.timestamp).toISOString(),
    })
  }

  if (failedChapters.length > 0) {
    return { attempted: pending.length, failedChapters }
  }

  const pin = getOfflineProject(projectId)
  const shell = readProjectShell(projectId)
  await downloadProjectForOffline({
    projectId,
    title: pin?.title || shell?.title || 'Proyecto sin título',
    type: pin?.type || shell?.type || '',
    status: pin?.status || shell?.status || '',
  })
  return { attempted: pending.length, failedChapters: [] }
}

/** Remove every local chapter of a project, including edits that were not uploaded. */
export async function deleteLocalProjectCopy(projectId: string): Promise<void> {
  await BackupService.whenReady()
  for (const backup of BackupService.listChapterBackups()) {
    if (backup.projectId !== projectId) continue
    BackupService.removeChapterBackup(backup.projectId, backup.chapterId)
  }
  removeOfflineProject(projectId)
}

/** Drop the clean offline mirror. Chapters with unsynced edits stay on the device. */
export async function removeOfflineAvailability(projectId: string): Promise<void> {
  await BackupService.whenReady()
  for (const backup of BackupService.listChapterBackups()) {
    if (backup.projectId !== projectId) continue
    if (isBackupPendingUpload(backup)) continue
    BackupService.removeChapterBackup(backup.projectId, backup.chapterId)
  }
  removeOfflineProject(projectId)
}
